import React, { useState } from 'react';
import { format } from 'date-fns';

export default function StaffTermsForm({ onSubmit, submitting = false }) {
  const [accepted, setAccepted] = useState(false);
  const effectiveDate = format(new Date(), 'd MMMM yyyy');

  const handleTosSubmit = async (e) => {
    e.preventDefault();
    if (!accepted || submitting) {
      return;
    }
    await onSubmit();
  };

  const PrivacyPolicy = () => (
    <section className="mb-8">
      <h1 className="text-2xl font-bold mb-3 text-blue-700">Privacy Policy</h1>
      <p className="text-base mb-2">
        <strong>Effective Date:</strong>{' '}
        <span className="font-normal">{format(new Date(), 'd MMMM yyyy')}</span>
      </p>
      <p className="text-base mb-2">
        <strong>Company:</strong>{' '}
        <span className="font-normal">InsightOS Ltd</span>
      </p>
      <p className="text-base mb-2">
        <strong>Company Number:</strong>{' '}
        <span className="font-normal">17115111</span>
      </p>
      <p className="text-base mb-6">
        <strong>Jurisdiction:</strong>{' '}
        <span className="font-normal">United Kingdom</span>
      </p>
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-2 text-blue-600">1. Introduction</h2>
        <p className="mb-4">
          We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our platform.
        </p>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">2. Information We Collect</h2>
        <ul className="list-disc ml-8 mb-4 space-y-1">
          <li><strong>Personal Data:</strong> Information you provide during registration or use, such as name, email address, company name, and contact details.</li>
          <li><strong>Usage Data:</strong> Data about how you use the platform, including login times, pages viewed, and features used.</li>
          <li><strong>Cookies and Tracking:</strong> We use cookies and similar technologies to improve your experience and analyze platform usage.</li>
        </ul>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">3. How We Use Your Information</h2>
        <ul className="list-disc ml-8 mb-4 space-y-1">
          <li>To provide and maintain our services</li>
          <li>To process billing and payments securely via Stripe</li>
          <li>To communicate important updates and support</li>
          <li>To improve and personalize your experience</li>
          <li>To comply with legal obligations</li>
        </ul>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">4. Legal Basis for Processing</h2>
        <p className="mb-4">
          We process your data based on your consent (e.g., when you accept the Terms), contractual necessity (providing the service), and legitimate interests (improving the platform, security).
        </p>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">5. Data Sharing and Disclosure</h2>
        <ul className="list-disc ml-8 mb-4 space-y-1">
          <li>We do <strong>not</strong> sell or rent your personal data.</li>
          <li>We share data only with trusted service providers (e.g., Stripe for payments, hosting providers) under strict confidentiality.</li>
          <li>We use Bunny CDN (Bunny.net) as a data processor to store, deliver, and process uploaded assets including images, videos, and documents required for platform features.</li>
          <li>We may disclose information to comply with legal obligations or protect our rights.</li>
        </ul>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">6. Data Security</h2>
        <ul className="list-disc ml-8 mb-4 space-y-1">
          <li>We implement appropriate technical and organizational measures to protect your data, including encryption in transit and at rest.</li>
          <li>Access to personal data is limited to authorized personnel only.</li>
        </ul>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">7. Data Retention</h2>
        <p className="mb-4">
          We retain your data for as long as necessary to provide our services and comply with legal obligations. Upon account deletion or termination, your data will be securely deleted unless retention is required by law.
        </p>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">8. Your Rights</h2>
        <p className="mb-2">Under GDPR, you have the right to:</p>
        <ul className="list-disc ml-8 mb-4 space-y-1">
          <li>Access your personal data</li>
          <li>Request correction or deletion</li>
          <li>Object to or restrict processing</li>
          <li>Data portability</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p className="mb-4">
          To exercise these rights, contact us at: <a href="mailto:support@insightos.co.uk" className="text-blue-700 underline">support@insightos.co.uk</a>
        </p>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">9. Cookies and Tracking Technologies</h2>
        <p className="mb-4">
          We use cookies to enhance your experience. You can manage cookie preferences through your browser settings. For detailed information, see our Cookie Policy.
        </p>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">10. International Transfers</h2>
        <p className="mb-4">
          Your data is stored and processed within the UK. If we transfer data outside the UK, appropriate safeguards will be in place.
        </p>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">11. Changes to This Privacy Policy</h2>
        <p className="mb-4">
          We may update this policy occasionally. Changes will be posted here with an updated effective date. Continued use of the platform means acceptance of the updated policy.
        </p>

        <h2 className="text-xl font-semibold mb-2 text-blue-600">12. Contact Us</h2>
        <p>
          For any questions or concerns regarding your privacy, please contact:<br />
          <strong>Data Protection Officer:</strong> Ashley Cook<br />
          <strong>Email:</strong> <a href="mailto:support@insightos.co.uk" className="text-blue-700 underline">support@insightos.co.uk</a>
        </p>
      </section>
    </section>
  );

  // DPA Component - Data Processing Agreement
  const DPA = () => (
    <section className="mb-8">
      <h1 className="text-2xl font-bold mb-3 text-blue-700">Data Processing Agreement (DPA)</h1>
      <p className="text-base mb-2">
        <strong>Effective Date:</strong> <span className="font-normal">{format(new Date(), 'd MMMM yyyy')}</span>
      </p>
      <p className="text-base mb-6">
        This Data Processing Agreement ("DPA") is entered into by and between Insightos ("Processor") and the entity agreeing to these terms ("Controller") and forms part of the Master Services Agreement or Terms of Service ("Agreement") between the parties.
      </p>

      <h2 className="text-xl font-semibold mb-2 text-blue-600">1. Scope and Role Definitions</h2>
      <ul className="list-disc ml-8 mb-4 space-y-1">
        <li><strong>Roles:</strong> Controller acts as the data exporter. Insightos acts as the data importer.</li>
        <li><strong>Compliance:</strong> Both parties will comply with all applicable global data protection laws (including GDPR).</li>
        <li><strong>Instructions:</strong> Insightos will only process personal data based on written instructions from the Controller.</li>
      </ul>

      <h2 className="text-xl font-semibold mb-2 text-blue-600">2. Technical and Organizational Security Measures</h2>
      <ul className="list-disc ml-8 mb-4 space-y-1">
        <li><strong>Infrastructure & OS:</strong> Data is hosted using IONOS Cloud enterprise infrastructure on secure Ubuntu Linux servers.</li>
        <li><strong>Server Location:</strong> All application servers and underlying databases are strictly located and hosted within the European Union (EU).</li>
        <li><strong>Management Platform:</strong> Server configuration, security updates, and access controls are managed via the Plesk platform.</li>
        <li><strong>Encryption Baseline:</strong> All personal, financial, and sensitive medical data is strictly encrypted at rest using industrial-grade AES-256 encryption standards.</li>
        <li><strong>Authentication Governance:</strong> Multi-Factor Authentication (MFA) is enforced as standard for all administrative access.</li>
        <li><strong>Security Frameworks:</strong> Insightos is fully compliant with the ISO 27001 security framework and is actively working towards formal accreditation.</li>
      </ul>

      <h2 className="text-xl font-semibold mb-2 text-blue-600">3. Sub-processors</h2>
      <ul className="list-disc ml-8 mb-4 space-y-1">
        <li><strong>Approved Directory:</strong> The current approved sub-processors are:</li>
        <ul className="list-disc ml-8 space-y-1">
          <li>IONOS Cloud (Infrastructure and EU-based data hosting)</li>
          <li>Plesk (Server administration and security maintenance)</li>
          <li>Stripe (Subscription payment processing and billing portal)</li>
          <li>Google LLC (Google Meet video conferencing and communications)</li>
          <li>Bunny Way d.o.o. / bunny.net (CDN and secure media/profile image storage)</li>
        </ul>
        <li><strong>Notification of Changes:</strong> Insightos will notify the Controller of any changes to sub-processors at least 14 days in advance.</li>
      </ul>

      <h2 className="text-xl font-semibold mb-2 text-blue-600">4. Data Subject Rights & Incident Notification</h2>
      <ul className="list-disc ml-8 mb-4 space-y-1">
        <li><strong>Assistance:</strong> Insightos will provide reasonable assistance to help the Controller respond to data subjects exercising their legal privacy rights.</li>
        <li><strong>Breach Notification:</strong> Insightos will notify the Controller without undue delay, and no later than 48 hours, after becoming aware of any accidental or unlawful personal data breach.</li>
        <li><strong>Data Protection Contact:</strong> Insightos has designated an official Data Protection Officer. The DPO can be reached directly at ashleycook@insightos.co.uk.</li>
      </ul>

      <h2 className="text-xl font-semibold mb-2 text-blue-600">5. Term and Data Deletion</h2>
      <ul className="list-disc ml-8 mb-4 space-y-1">
        <li><strong>Deletion Timeline:</strong> All company profiles, HR files, financial logs, and medical records will be permanently and irreversibly purged from Insightos servers and backups within 30 days of contract termination.</li>
      </ul>

      <h2 className="text-xl font-semibold mb-2 text-blue-600">Schedule A: Details of Processing</h2>
      <ul className="list-disc ml-8 mb-4 space-y-1">
        <li><strong>Categories of Data Subjects:</strong> Controller's employees, contractors, and corporate end-users.</li>
        <li><strong>Types of Personal Data:</strong> Corporate profiles, HR records, employee financial details, video/audio metadata, and user profile pictures.</li>
        <li><strong>Special Categories of Data (Sensitive Data):</strong> Employee medical histories, health metrics, and occupational health data.</li>
        <li><strong>Nature of Processing:</strong> Storage, hosting, video consultation hosting, image delivery optimization, and technical processing necessary to deliver the Insightos B2B SaaS platform services.</li>
      </ul>
    </section>
  );

  return (
    <form
      className="mx-auto flex min-h-screen w-full max-w-none flex-col bg-white px-4 py-5 sm:px-6 sm:py-6 lg:px-10 lg:py-8"
      onSubmit={handleTosSubmit}
    >
      <h1
        id="modal-title"
        className="mb-6 text-center text-2xl font-extrabold tracking-tight text-blue-800 sm:mb-8 sm:text-3xl lg:text-4xl"
      >
        Terms of Service & Privacy Policy
      </h1>
      <div
        id="modal-description"
        className="prose prose-sm sm:prose-base max-w-none flex-1 overflow-y-auto text-gray-900 mb-8 pr-1 sm:pr-2"
        style={{ whiteSpace: 'pre-wrap' }}
      >
        <section className="mb-8">
          <p className="mb-2 text-sm sm:text-base"><strong>Effective Date:</strong> <span className="font-normal">{effectiveDate}</span></p>
          <p className="mb-2 text-sm sm:text-base"><strong>Company:</strong> <span className="font-normal">InsightOS Ltd</span></p>
          <p className="mb-2 text-sm sm:text-base"><strong>Company Number:</strong> <span className="font-normal">17115111</span></p>
          <p className="mb-2 text-sm sm:text-base"><strong>Jurisdiction:</strong> <span className="font-normal">United Kingdom</span></p>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">0. Definitions</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li><strong>"Platform"</strong> refers to the web-based software service provided by InsightOS.</li>
            <li><strong>"User"</strong> refers to any individual authorized to access and use the Platform under a paid or trial account.</li>
            <li><strong>"Content"</strong> means all text, data, files, and other materials submitted or generated via the Platform.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">1. Use of the Service</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li><strong>Business Use Only:</strong> The Service is licensed solely for business use by the registered subscriber.</li>
            <li><strong>User Accounts:</strong> Each login is licensed to a single named user. Sharing credentials or creating shared team logins is strictly prohibited.</li>
            <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
            <li>You may not use the platform for any unlawful, defamatory, or harmful activities.</li>
            <li>We reserve the right to suspend or terminate accounts that violate these terms.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">2. Free Trial</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>A 14-day free trial is provided for up to 5 users.</li>
            <li>After the trial, continued use requires adding a valid payment method.</li>
            <li>Failure to add payment after the trial period will result in suspension of access.</li>
            <li>By using the trial, you agree to all terms listed in this document.</li>
            <li>We reserve the right to modify or cancel the trial at any time.</li>
            <li>If you are not satisfied with the trial, you may cancel at any time before the trial period ends.</li>
            <li>If you choose to pay for the service bypassing the trial you will still have 5 free users available but for the first month only.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">3. Subscription, Billing & Cancellation</h2>
          <ul className="list-disc ml-8 space-y-2">
            <ul className="list-disc ml-8 space-y-2">
              <li>You may choose a <strong>14-day free trial</strong> for up to <strong>5 users</strong> with no card required. After the trial ends, you will be asked to add a payment method to continue.</li>
              <li>If you prefer to skip the trial, you can pay immediately to unlock full access and add more than 5 users from day one.</li>
              <li>Subscription is <strong>£15 per user per month</strong>, billed monthly in advance via Stripe, our secure payment provider.</li>
              <li>Billing is based on the number of active users (those with valid login access) at the time of each billing cycle.</li>
              <li>You may upgrade or downgrade your user count at any time. Changes will take effect in the next month's invoice.</li>
              <li>Added extras such as additional AI token purchases are charged separately as one-off payments and are <strong>not</strong> included in the base subscription.</li>
              <li>You must maintain a valid payment method and authorize recurring billing through Stripe.</li>
              <li>A minimum of <strong>30 days written notice</strong> is required to cancel your subscription.</li>
              <li>If cancellation notice overlaps your billing cycle, a final payment will still be charged.</li>
              <li>No refunds are provided if the platform has been accessed beyond the 14-day trial period.</li>
              <li>All fees are non-refundable unless the account remained entirely inactive during the billing cycle.</li>
            </ul>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">4. AI-Generated Content & Accuracy</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>The platform uses AI APIs (e.g., OpenAI) to generate suggestions and insights.</li>
            <li>AI outputs may be inaccurate or incomplete and should be reviewed by a human.</li>
            <li>We hold no liability for actions taken based on AI-generated content.</li>
            <li>You acknowledge and accept that AI is not always correct.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">5. Intellectual Property, Piracy & Restrictions</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>All source code, features, and content are owned by <strong>InsightOS Ltd</strong>.</li>
            <li>Copying, reverse engineering, cloning, reselling, or reproducing any part of the platform is prohibited.</li>
            <li>Any attempt to build a competing product using the platform constitutes a breach of these terms.</li>
            <li>Data scraping, screen scraping, or automated extraction of content or system features is strictly prohibited.</li>
            <li>Violations will result in legal action and potential damages claims.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">6. Data Ownership & Usage</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>You retain full ownership of your business data uploaded or generated.</li>
            <li>We do not access, resell, or distribute your data without explicit permission.</li>
            <li>Your company remains responsible for the legality and accuracy of all uploaded and generated content.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">7. System Access, Downtime & Vendors</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>We aim for high uptime but do not guarantee uninterrupted access.</li>
            <li>We are not liable for:
              <ul className="list-disc ml-8 space-y-1">
                <li>Downtime due to internet connectivity issues on your end.</li>
                <li>Third-party vendor outages (e.g., IONOS Cloud VPS, used to host our UK servers).</li>
                <li>Force majeure or unforeseen disruptions.</li>
              </ul>
            </li>
            <li>You agree we are not responsible for losses resulting from third-party service failures.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">8. Security, Privacy & Encryption</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>All user data is encrypted in transit and at rest.</li>
            <li>MFA (Multi-Factor Authentication) is supported for secure login.</li>
            <li>Password resets are self-managed and secured.</li>
            <li>We use GDPR-compliant infrastructure and hosting within the United Kingdom.</li>
            <li>We conduct regular security audits and assessments to ensure platform integrity.</li>
            <li>We will notify you of any significant security incidents that may affect your data.</li>
            <li>We are committed to protecting your privacy and data security.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">9. Misuse & Harmful Content</h2>
          <ul className="list-disc ml-8 space-y-2 text-gray-800">
            <li>You agree not to use the platform in any way that is unlawful, abusive, or in violation of these terms.</li>
            <li>
              Prohibited activities include, but are not limited to:
              <ul className="list-disc ml-8 space-y-1">
                <li>Using the platform for any unlawful, illegal, fraudulent, or harmful purpose.</li>
                <li>Submitting, uploading, or distributing content that is:
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Defamatory, abusive, harassing, threatening, or hateful.</li>
                    <li>Racist, sexist, discriminatory, or otherwise offensive to protected classes.</li>
                    <li>Obscene, pornographic, or sexually explicit in nature.</li>
                    <li>Promoting violence, terrorism, or criminal behavior.</li>
                  </ul>
                </li>
                <li>Uploading, distributing, or using copyrighted or proprietary materials without express legal permission or license.</li>
                <li>Attempting to exploit, reverse-engineer, clone, copy, pirate, or tamper with the platform or its underlying codebase.</li>
                <li>Engaging in unauthorized access, system abuse, or any activity that disrupts service availability for others.</li>
                <li>Scraping, harvesting, or collecting data from other users or the system without explicit written consent.</li>
                <li>Using automated bots, scripts, or systems that interfere with the normal functioning or performance of the platform.</li>
                <li>Publishing, promoting, or sharing any material that may expose InsightOS Ltd to legal, financial, or reputational risk.</li>
                <li>Engaging in any other activity that could harm the platform or its users.</li>
              </ul>
            </li>
            <li>
              We reserve the right to investigate and take legal action (including immediate suspension or termination of access) against any user found to be engaging in prohibited or harmful behavior.
            </li>
            <li>
              You are solely responsible for any content submitted through your account, and you acknowledge that violations of this section may lead to legal liability.
            </li>
            <li>
              We may report illegal activities to appropriate law enforcement or regulatory authorities if necessary.
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">10. GDPR Compliance</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>We are fully compliant with the General Data Protection Regulation (GDPR).</li>
            <li>You may request to access, correct, or delete your data at any time.</li>
            <li>We will not share your data with third parties without your explicit consent, except as required by law.</li>
            <li>We will notify you of any data breaches within 72 hours as required by GDPR.</li>
            <li>We will process your data only for the purposes of providing the service and improving the platform.</li>
            <li>During sign-up and acceptance of these Terms, certain personal data necessary for business operations will be collected and securely stored. This does not include sensitive payment information such as bank details, which are handled exclusively by our trusted payment provider, Stripe. You have the right to request access to your personal data at any time through a Subject Access Request (SAR). For any data protection concerns or requests, please contact our Data Protection Officer, Ashley Cook. For full details on data collection and usage, please refer to our Privacy Policy.</li>
            <li>Requests should be sent to <a href="mailto:support@insightos.co.uk" className="text-blue-700 underline">support@insightos.co.uk</a>.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">11. Termination & Suspension</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>We may suspend or terminate accounts if:
              <ul className="list-disc ml-8 space-y-1">
                <li>You violate these terms.</li>
                <li>Your actions pose a legal or security risk to the platform.</li>
                <li>You engage in any prohibited activity or breach data policies.</li>
              </ul>
            </li>
            <li>Outstanding subscription fees remain due after suspension or termination.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">12. Liability Limitations</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>The platform is provided "as is" and "as available," without warranties of any kind, express or implied.</li>
            <li>We expressly disclaim all warranties, including but not limited to merchantability, fitness for a particular purpose, and non-infringement.</li>
            <li>We do not guarantee that the platform will be uninterrupted, error-free, secure, or free from viruses or harmful components.</li>
            <li>You agree to use the platform at your own risk.</li>
            <li>We are not liable for any indirect, incidental, special, or consequential damages, including loss of data, profits, or business opportunities.</li>
            <li>Our total liability is limited to the subscription fees you paid in the three months prior to the incident giving rise to the claim.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">13. Marketing & Use of Your Logo</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>With your permission, we may:
              <ul className="list-disc ml-8 space-y-1">
                <li>Display your company logo on our website or marketing material as a user.</li>
                <li>You may revoke this consent at any time by contacting <a href="mailto:support@insightos.co.uk" className="text-blue-700 underline">support@insightos.co.uk</a>.</li>
              </ul>
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">14. Legal Jurisdiction</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>These Terms are governed by the laws of England and Wales.</li>
            <li>Any disputes will be resolved under UK legal jurisdiction.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">15. Additional Protections</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>The platform is provided "as-is", and we do not guarantee compatibility with all browsers or systems.</li>
            <li>By using our Service, you accept:
              <ul className="list-disc ml-8 space-y-1">
                <li>That the AI features are evolving and may occasionally produce errors.</li>
                <li>That piracy, cloning, or misuse will be prosecuted to the full extent of UK intellectual property law.</li>
                <li>The system is strictly for legitimate business use only.</li>
              </ul>
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">16. Indemnification</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>You agree to indemnify, defend, and hold harmless InsightOS Ltd, its directors, officers, employees, and agents from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including legal fees) arising from:
              <ul className="list-disc ml-8 space-y-1">
                <li>Your use of and access to the Platform.</li>
                <li>Your violation of any term of these Terms.</li>
                <li>Your violation of any third-party right, including copyright, property, or privacy rights.</li>
              </ul>
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">17. Changes to These Terms</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>We may update these Terms from time to time to reflect legal, operational, or service changes.</li>
            <li>Any changes will be effective upon posting to the Platform. Continued use of the service constitutes acceptance of the updated Terms.</li>
            <li>We encourage users to review these Terms periodically.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">18. Cookies & Tracking Technologies</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>We may use cookies and similar technologies to improve your experience, analyze traffic, and deliver targeted content.</li>
            <li>By using our platform, you consent to our use of cookies in accordance with our Cookie Policy.</li>
            <li>You may manage your preferences through your browser settings.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">19. Force Majeure</h2>
          <ul className="list-disc ml-8 space-y-2">
            <li>We are not liable for any delay or failure to perform resulting from causes outside our reasonable control, including acts of God, war, terrorism, labor shortages, internet outages, or service provider failures.</li>
          </ul>
        </section>

        {/* Google API Data Disclosure Section */}
        <section className="mb-8 border-t-2 border-blue-200 pt-6">
          <h2 className="mb-3 text-xl font-bold text-blue-700 sm:text-2xl">20. Google API Data Disclosure</h2>
          <div className="mb-4 rounded-r-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="mb-2 text-sm font-semibold text-blue-700">🔒 Privacy & Data Protection</p>
          </div>
          <ul className="list-disc ml-8 space-y-3">
            <li>
              <strong>Data Access:</strong> InsightOS explicitly requests permission to access your Google Calendar account solely to create, schedule, and generate Google Meet video conferencing links for your corporate events.
            </li>
            <li>
              <strong>Data Storage & Retention:</strong> InsightOS does not read, download, store, or track your existing Google emails, files, or personal calendar entries. We only write data to your account when you explicitly initiate a meeting creation within our platform.
            </li>
            <li className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
              <strong>Limited Use Clause:</strong> InsightOS's use and transfer of information received from Google APIs to any other app will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700 underline">Google API Services User Data Policy</a>, including the Limited Use requirements.
            </li>
            <li className="mt-2 text-sm text-gray-600">
              <strong>Compliance:</strong> We are committed to protecting your Google data in accordance with Google's API Services User Data Policy and all applicable privacy regulations.
            </li>
          </ul>
        </section>

        {/* Data Processing Agreement (DPA) */}
        <DPA />

        <PrivacyPolicy />
      </div>

      <div className="mb-6 flex items-start gap-3 sm:mb-8">
        <input
          type="checkbox"
          id="accept"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="h-5 w-5 accent-blue-700"
          required
        />
        <label htmlFor="accept" className="font-semibold text-gray-900">
          I have read and accept the Terms of Service & Privacy Policy
        </label>
      </div>

      <div className="sticky bottom-0 -mx-4 border-t border-blue-100 bg-white/95 px-4 pb-2 pt-4 backdrop-blur-sm sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
        <div className="mx-auto flex w-full max-w-5xl justify-center">
          <button
            type="submit"
            disabled={!accepted || submitting}
            className={`w-full max-w-sm rounded-lg bg-blue-700 px-8 py-3 text-sm font-bold text-white shadow-md transition-all duration-150 hover:bg-blue-800 sm:text-base ${
              !accepted || submitting ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            {submitting ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-gray-500">
        By clicking <strong>Continue</strong>, you agree to our{' '}
        <a
          href="/privacy-policy"
          className="font-semibold text-blue-700 hover:underline"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href="/terms-of-service"
          className="font-semibold text-blue-700 hover:underline"
        >
          Terms of Service
        </a>
        .
      </p>
      <p className="mt-2 text-center text-xs text-gray-500">
        If you do not agree to these terms, you may not use the service.
      </p>
      <p className="mt-2 text-center text-xs text-gray-500">
        For any questions, please contact us at{' '}
        <a
          href="mailto:support@insightos.co.uk"
          className="font-semibold text-blue-700 hover:underline"
        >
          support@insightos.co.uk
        </a>
        .
      </p>
    </form>
  );
}